/**
 * PDF Data Service - Collects and organizes all data needed for PDF generation
 */
import { api } from '../lib/api';

// Type definitions for PDF export data
export interface OrganizationData {
  id: string;
  companyName: string;
  companyMotto?: string;
  companyWebsite?: string;
  companyPhone?: string;
  companyABN?: string;
  companyACN?: string;
  logoUrl?: string;
  otherInfo?: string;
  exportDate: string;
  exportedBy: string;
}

export interface TileData {
  id: string;
  type: string;
  title: string;
  description?: string;
  position: { row: number; column: number };
  size: { width: number; height: number };
  config: any;
  content?: any; // Rich text content for content tiles
  linkedPageId?: string; // For nested_page tiles
}

export interface PageData {
  id: string;
  title: string;
  description?: string;
  tiles: TileData[];
  children: PageData[]; // Child pages for nested structure
  level: number; // Hierarchy level (0 = homepage, 1 = child, etc.)
}

export interface PDFExportData {
  organization: OrganizationData;
  content: PageData;
  tableOfContents: TOCEntry[];
}

export interface TOCEntry {
  title: string;
  level: number;
  pageNumber?: number; // Will be set during PDF generation
  children?: TOCEntry[];
}

export class PDFDataService {
  
  /**
   * Main method to collect all data needed for PDF export
   */
  static async collectExportData(
    organizationId: string,
    currentUser: any
  ): Promise<PDFExportData> {
    try {
      
      // Step 1: Get organization profile and branding
      const organizationData = await this.getOrganizationData(organizationId, currentUser);
      
      // Step 2: Build complete page hierarchy starting from homepage
      const contentData = await this.buildPageHierarchy(organizationId);
      
      // Step 3: Generate table of contents structure
      const tableOfContents = this.generateTableOfContents(contentData);
      
      console.log('✅ PDF data collection complete');
      
      return {
        organization: organizationData,
        content: contentData,
        tableOfContents
      };
      
    } catch (error) {
      console.error('❌ PDF data collection failed:', error);
      throw new Error(`Failed to collect export data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  /**
   * Get organization profile and branding information
   */
  private static async getOrganizationData(
    organizationId: string, 
    currentUser: any
  ): Promise<OrganizationData> {
    const response = await api.get(`/organizations/profile/${organizationId}`);
    const profile = response.data.data;
    
    return {
      id: organizationId,
      companyName: profile.companyName || 'Organization',
      companyMotto: profile.companyMotto,
      companyWebsite: profile.companyWebsite,
      companyPhone: profile.companyPhone,
      companyABN: profile.companyABN,
      companyACN: profile.companyACN,
      logoUrl: profile.logoUrl,
      otherInfo: profile.otherInfo,
      exportDate: new Date().toLocaleString(),
      exportedBy: `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}`.trim() || currentUser?.email || 'Unknown User'
    };
  }
  
  /**
   * Build complete page hierarchy starting from homepage
   */
  private static async buildPageHierarchy(organizationId: string): Promise<PageData> {
    // Get homepage first
    const homepageResponse = await api.get('/pages/homepage');
    const homepage = homepageResponse.data.data;
    
    // Get all pages for the organization
    const pagesResponse = await api.get('/pages');
    const allPages = pagesResponse.data.data;
    
    // Build hierarchy recursively starting from homepage
    const homepageData = await this.buildPageData(homepage, allPages, 0);
    
    return homepageData;
  }
  
  /**
   * Recursively build page data with tiles and child pages
   */
  private static async buildPageData(
    page: any, 
    allPages: any[], 
    level: number
  ): Promise<PageData> {
    
    // Get page with tiles using the correct endpoint
    const pageResponse = await api.get(`/pages/${page.id}`);
    const pageData = pageResponse.data.data;
    const tiles = pageData.tiles || [];
    
    // Sort tiles by type first, then by position (column first, then row)
    const getTileTypeOrder = (type: string): number => {
      switch (type) {
        case 'content': return 1;
        case 'roadmap': return 2;
        case 'form': return 3;
        case 'external_link': return 4;
        case 'nested_page': return 5;
        case 'shortcut': return 6;
        case 'register': return 7;
        case 'workflow': return 8;
        default: return 9;
      }
    };

    const sortedTiles = tiles.sort((a: any, b: any) => {
      // First sort by tile type
      const typeOrderA = getTileTypeOrder(a.type);
      const typeOrderB = getTileTypeOrder(b.type);
      
      if (typeOrderA !== typeOrderB) {
        return typeOrderA - typeOrderB;
      }
      
      // Then sort by position (column first, then row)
      if (a.position.column !== b.position.column) {
        return a.position.column - b.position.column;
      }
      return a.position.row - b.position.row;
    });
    
    // Process tiles and get their content
    const processedTiles = await Promise.all(
      sortedTiles.map((tile: any) => this.processTile(tile))
    );
    
    
    // Find child pages (pages that have this page as parent)
    const childPages = allPages.filter(p => p.parentPageId === page.id);
    
    // Recursively build child page data
    const children = await Promise.all(
      childPages.map(childPage => this.buildPageData(childPage, allPages, level + 1))
    );
    
    return {
      id: page.id,
      title: page.title,
      description: page.description,
      tiles: processedTiles,
      children,
      level
    };
  }
  
  /**
   * Process individual tile based on its type
   */
  private static async processTile(tile: any): Promise<TileData> {
    const baseTileData: TileData = {
      id: tile.id,
      type: tile.type,
      title: tile.title,
      description: tile.description,
      position: tile.position,
      size: tile.size,
      config: tile.config,
      linkedPageId: tile.linkedPageId
    };
    
    // Add type-specific content processing
    switch (tile.type) {
      case 'content':
      case 'roadmap':
        // Get rich text content - try multiple approaches
        console.log(`📄 Analyzing content tile "${tile.title}":`, {
          hasConfig: !!tile.config,
          configKeys: tile.config ? Object.keys(tile.config) : [],
          fullConfig: tile.config,
          tileId: tile.id,
          linkedItem: tile.config?.linkedItem,
          content: tile.config?.content,
          contentId: tile.config?.contentId
        });
        
        let contentId = null;
        
        // Try different ways to find contentId
        if (tile.config?.contentId) {
          contentId = tile.config.contentId;
        } else if (tile.config?.content?.id) {
          contentId = tile.config.content.id;
        } else if (tile.config?.linkedItem?.id && tile.config?.linkedItem?.type === 'content') {
          contentId = tile.config.linkedItem.id;
        } else if (tile.config?.linkedContentId) {
          contentId = tile.config.linkedContentId;
        } else if (tile.config?.data?.contentId) {
          contentId = tile.config.data.contentId;
        } else if (tile.config?.settings?.contentId) {
          contentId = tile.config.settings.contentId;
        }
        
        if (contentId) {
          try {
            console.log(`📄 Fetching external content for tile "${tile.title}" with contentId:`, contentId);
            const contentResponse = await api.get(`/content/${contentId}`);
            baseTileData.content = contentResponse.data.data;
            console.log(`✅ External content loaded for "${tile.title}":`, {
              hasContent: !!baseTileData.content,
              contentLength: baseTileData.content?.body?.length || 0,
              contentPreview: baseTileData.content?.body?.substring(0, 100) || 'No body content'
            });
          } catch (error) {
            console.warn(`❌ Failed to load external content for tile ${tile.id}:`, error);
            baseTileData.content = null;
          }
        } else if (tile.config?.body) {
          // Handle inline content stored in tile.config.body
          console.log(`📄 Using inline content for tile "${tile.title}"`);
          baseTileData.content = {
            title: tile.title,
            body: tile.config.body,
            description: tile.description
          };
          console.log(`✅ Inline content loaded for "${tile.title}":`, {
            hasContent: !!baseTileData.content,
            contentLength: baseTileData.content?.body?.length || 0,
            contentPreview: baseTileData.content?.body?.substring(0, 100) || 'No body content'
          });
        } else {
          console.log(`⚠️ No content found for tile "${tile.title}" - checking config structure`);
          console.log(`🔍 Tile config analysis for "${tile.title}":`, {
            hasLinkedItem: !!tile.config?.linkedItem,
            linkedItemType: tile.config?.linkedItem?.type,
            linkedItemId: tile.config?.linkedItem?.id,
            hasInlineBody: !!tile.config?.body,
            bodyLength: tile.config?.body?.length || 0,
            allConfigKeys: tile.config ? Object.keys(tile.config) : []
          });
        }
        break;
        
      case 'form':
        // Get form structure
        if (tile.config?.formId) {
          try {
            const formResponse = await api.get(`/forms/${tile.config.formId}`);
            baseTileData.content = formResponse.data.data;
          } catch (error) {
            console.warn(`Failed to load form for tile ${tile.id}:`, error);
          }
        }
        break;
        
      case 'external_link':
        // External link tiles use config.url directly
        break;
        
      case 'nested_page':
        // Nested page tiles will be handled during hierarchy building
        break;
        
      case 'shortcut':
        // Shortcut tiles link to other items - include basic info
        baseTileData.content = {
          linkedItem: tile.config?.linkedItem,
          shortcutType: 'shortcut'
        };
        break;
        
      case 'register':
        // Register tiles - include basic configuration
        baseTileData.content = {
          registerType: 'register',
          config: tile.config
        };
        break;
        
      case 'workflow':
        // Workflow tiles - include basic configuration
        baseTileData.content = {
          workflowType: 'workflow',
          config: tile.config
        };
        break;
        
      default:
        console.warn(`Unknown tile type: ${tile.type}`);
        // Include basic tile info for unknown types
        baseTileData.content = {
          unknownType: tile.type,
          config: tile.config
        };
    }
    
    console.log(`🔍 Returning tile data for "${tile.title}":`, {
      hasContent: !!baseTileData.content,
      contentKeys: baseTileData.content ? Object.keys(baseTileData.content) : 'No content',
      allKeys: Object.keys(baseTileData)
    });
    
    return baseTileData;
  }
  
  /**
   * Generate table of contents structure from page hierarchy
   */
  private static generateTableOfContents(pageData: PageData): TOCEntry[] {
    const toc: TOCEntry[] = [];
    
    // Add main page entry
    const pageEntry: TOCEntry = {
      title: pageData.title,
      level: pageData.level,
      children: []
    };
    
    // Add tile entries (only for tiles that create sections)
    pageData.tiles.forEach(tile => {
      if (this.shouldIncludeInTOC(tile)) {
        pageEntry.children!.push({
          title: tile.title,
          level: pageData.level + 1
        });
      }
    });
    
    // Add child page entries recursively
    pageData.children.forEach(childPage => {
      const childTOC = this.generateTableOfContents(childPage);
      pageEntry.children!.push(...childTOC);
    });
    
    toc.push(pageEntry);
    return toc;
  }
  
  /**
   * Determine if a tile should be included in table of contents
   */
  private static shouldIncludeInTOC(tile: TileData): boolean {
    // Include tiles that create content sections
    return [
      'content', 
      'roadmap', 
      'form', 
      'external_link', 
      'shortcut', 
      'register', 
      'workflow'
    ].includes(tile.type);
  }
}
